import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, X, Trophy } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import LoadMoreButton from '../components/LoadMoreButton';
import { authConfig } from '../config/auth';
import { userManager } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import type { Reward } from '@crewcast/shared';
import '../styles/rewards.css';

type EditableRewardFields = Pick<
  Reward,
  'title' | 'description' | 'points' | 'isActive'
>;

export default function RewardsPage() {
  usePageTitle('Rewards');
  const [claimingRewardId, setClaimingRewardId] = useState<string | null>(null);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalReward, setClaimModalReward] = useState<Reward | null>(null);
  const [claimModalError, setClaimModalError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [createForm, setCreateForm] = useState<EditableRewardFields>({
    title: '',
    description: '',
    points: 0,
    isActive: true,
  });
  const [pointsInput, setPointsInput] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const [togglingRewards, setTogglingRewards] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const navigate = useNavigate();
  const { token, user: currentUser, setUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isEmployee = currentUser?.role === 'employee';
  const currentPoints = currentUser?.points ?? 0;
  const pointsEnabled = currentUser?.companySettings?.pointsEnabled ?? true;

  const handleToggleRewards = async () => {
    setConfirmModalOpen(false);
    setTogglingRewards(true);
    try {
      if (!token) throw new Error('Not authenticated');

      const mergedSettings = {
        ...(currentUser?.companySettings ?? {}),
        pointsEnabled: !pointsEnabled,
      };

      const response = await fetch(`${authConfig.apiUrl}/api/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: mergedSettings }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update settings');
      }

      const updatedUser = await userManager.refreshUser(authConfig.apiUrl);
      if (updatedUser) setUser(updatedUser);
    } catch (err: any) {
      alert(err.message || 'Failed to update rewards setting');
    } finally {
      setTogglingRewards(false);
    }
  };

  const {
    items: rewards,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  } = usePaginatedFetch<Reward>({
    url: `${authConfig.apiUrl}/api/admin/rewards`,
    token,
    dataKey: 'rewards',
    limit: 20,
  });

  const handleCreateReward = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');

    // Validate points
    const points = Number(pointsInput);
    if (isNaN(points) || points < 0 || pointsInput === '') {
      setCreateError('Please enter a valid positive number for points');
      return;
    }

    try {
      if (!token) throw new Error('Not authenticated');

      let image: string | undefined;

      // Upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        const presignedResponse = await fetch(
          `${authConfig.apiUrl}/api/upload/presigned-url`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              fileName: imageFile.name,
              fileType: imageFile.type,
            }),
          }
        );

        if (!presignedResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { uploadUrl, publicUrl } = await presignedResponse.json();

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': imageFile.type,
          },
          body: imageFile,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        image = publicUrl;
        setUploadingImage(false);
      }

      const payload = {
        title: createForm.title.trim(),
        description: createForm.description?.trim() || undefined,
        points: points,
        image,
        isActive: createForm.isActive,
      };

      if (!payload.title) {
        throw new Error('Title is required');
      }

      const response = await fetch(`${authConfig.apiUrl}/api/admin/rewards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create reward');
      }

      if (data.reward) {
        refresh();
      }

      // Reset form
      setCreateForm({
        title: '',
        description: '',
        points: 0,
        isActive: true,
      });
      setPointsInput('');
      setImageFile(null);
      setImagePreview('');
      setCreating(false);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create reward');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteReward = async (rewardId: string) => {
    if (!window.confirm('Are you sure you want to delete this reward?')) return;

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/rewards/${rewardId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok && response.status !== 204) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete reward');
      }

      refresh();
    } catch (err: any) {
      alert(err.message || 'Failed to delete reward');
    }
  };

  const handleClaimReward = async (reward: Reward) => {
    try {
      if (!token) throw new Error('Not authenticated');

      setClaimingRewardId(reward.rewardId);
      setClaimModalReward(reward);
      setClaimModalError('');

      const response = await fetch(`${authConfig.apiUrl}/api/rewards/${reward.rewardId}/claim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to claim reward');
      }

      // Update local cached user points for immediate UI feedback
      if (currentUser && typeof data.remainingPoints === 'number') {
        setUser({ ...currentUser, points: data.remainingPoints });
      }

      setClaimModalOpen(true);
    } catch (err: any) {
      setClaimModalReward(reward);
      setClaimModalError(err.message || 'Failed to claim reward');
      setClaimModalOpen(true);
    } finally {
      setClaimingRewardId(null);
    }
  };

  if (loading) {
    return (
      <div className="rewards-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rewards-container">
        <div className="rewards-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="rewards-container">
      <div className="rewards-header">
        <div>
          <h1 className="rewards-title">Rewards</h1>
          <p className="rewards-subtitle">
            {rewards.length} {rewards.length === 1 ? 'reward' : 'rewards'} in
            your company
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {isEmployee && (
            <button
              className="rewards-secondary-btn"
              onClick={() => navigate('/claimed-rewards')}
            >
              My Claimed Rewards
            </button>
          )}
          {isAdmin && (
            <>
              <button
                className="rewards-secondary-btn"
                onClick={() => navigate('/admin/claimed-rewards')}
              >
                Claimed Rewards
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#666' }}>Rewards Store</span>
                <button
                  onClick={() => setConfirmModalOpen(true)}
                  disabled={togglingRewards}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 20,
                    border: 'none',
                    cursor: togglingRewards ? 'not-allowed' : 'pointer',
                    background: pointsEnabled ? '#00bf62' : '#9ca3af',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 13,
                    transition: 'background 0.2s',
                    opacity: togglingRewards ? 0.6 : 1,
                  }}
                  title={pointsEnabled ? 'Click to disable rewards store' : 'Click to enable rewards store'}
                >
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%', background: '#fff',
                    display: 'inline-block', flexShrink: 0,
                  }} />
                  {togglingRewards ? 'Saving…' : pointsEnabled ? 'On' : 'Off'}
                </button>
              </div>
              <button
                className="rewards-primary-btn"
                onClick={() => setCreating((open) => !open)}
              >
                {creating ? 'Cancel' : 'Create Reward'}
              </button>
            </>
          )}
        </div>
      </div>

      {isAdmin && creating && (
        <div className="rewards-card rewards-create-card">
          <h2 className="rewards-card-title">New Reward</h2>
          <form onSubmit={handleCreateReward} className="rewards-form">
            <div className="rewards-form-row">
              <label className="rewards-label">
                Title
                <input
                  type="text"
                  className="rewards-input"
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  required
                />
              </label>
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">
                Description
                <textarea
                  className="rewards-textarea"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                />
              </label>
            </div>

            <div className="rewards-form-row rewards-form-row-inline">
              <label className="rewards-label">
                Points
                <input
                  type="text"
                  className="rewards-input"
                  value={pointsInput}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setPointsInput(value);
                  }}
                  placeholder="0"
                  required
                />
              </label>
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">Status</label>
              <div className="rewards-radio-group">
                <label className="rewards-radio-label">
                  <input
                    type="radio"
                    name="create-status"
                    checked={!createForm.isActive}
                    onChange={() =>
                      setCreateForm((prev) => ({ ...prev, isActive: false }))
                    }
                  />
                  <span>Inactive</span>
                </label>
                <label className="rewards-radio-label">
                  <input
                    type="radio"
                    name="create-status"
                    checked={createForm.isActive}
                    onChange={() =>
                      setCreateForm((prev) => ({ ...prev, isActive: true }))
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="rewards-form-row">
              <label className="rewards-label">
                Reward Image (optional)
                <input
                  type="file"
                  accept="image/*"
                  className="rewards-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setImagePreview(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
              {imagePreview && (
                <div className="rewards-image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
            </div>

            {createError && (
              <div className="rewards-error-inline">{createError}</div>
            )}

            <div className="rewards-form-actions">
              <button 
                type="submit" 
                className="rewards-primary-btn"
                disabled={uploadingImage}
              >
                {uploadingImage ? 'Uploading...' : 'Save Reward'}
              </button>
            </div>
          </form>
        </div>
      )}

              {rewards.length === 0 ? (
        <div className="rewards-empty">
          <div className="rewards-empty-icon"><Trophy size={48} /></div>
          <h2 className="rewards-empty-title">No rewards yet</h2>
          <p className="rewards-empty-text">
            {isAdmin
              ? 'Create your first reward to start recognizing your team.'
              : 'Your company has not configured any rewards yet.'}
          </p>
        </div>
      ) : (
        <div className="rewards-grid">
          {rewards.map((reward) => (
            <div key={reward.rewardId} className="rewards-card">
              {reward.image && (
                <div className="rewards-card-image">
                  <img src={reward.image} alt={reward.title} />
                </div>
              )}
              
              <div className="rewards-card-header">
                <h3 className="rewards-card-title">{reward.title}</h3>
                <span
                  className={`rewards-status-badge ${
                    reward.isActive ? 'active' : 'inactive'
                  }`}
                >
                  {reward.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="rewards-card-meta">
                <span className="rewards-points">{reward.points} pts</span>
                {reward.createdAt && (
                  <span className="rewards-created-at">
                    Created{' '}
                    {new Date(reward.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                )}
              </div>

              <div className="rewards-card-actions">
                <button
                  className="rewards-secondary-btn"
                  onClick={() => navigate(`/rewards/${reward.rewardId}`)}
                >
                  View details
                </button>
                {isEmployee && reward.isActive && (
                  <button
                    className="rewards-primary-btn"
                    disabled={
                      claimingRewardId === reward.rewardId ||
                      (reward.points ?? 0) > currentPoints
                    }
                    onClick={() => handleClaimReward(reward)}
                  >
                    {claimingRewardId === reward.rewardId
                      ? 'Claiming...'
                      : (reward.points ?? 0) > currentPoints
                      ? 'Not enough points'
                      : 'Claim'}
                  </button>
                )}
                {isAdmin && (
                  <button
                    className="rewards-danger-btn"
                    onClick={() => handleDeleteReward(reward.rewardId)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {claimModalOpen && claimModalReward && (
        <div
          className="crewcast-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="claim-reward-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setClaimModalOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setClaimModalOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="crewcast-modal-card">
            <div className="crewcast-modal-header">
              <div>
                <h2 id="claim-reward-title" className="crewcast-modal-title">
                  {claimModalError ? 'Couldn’t claim reward' : 'Reward claimed!'}
                </h2>
                <div className="crewcast-modal-subtitle">
                  {claimModalReward.title} • {claimModalReward.points} pts
                </div>
              </div>
              <button
                type="button"
                className="crewcast-modal-close"
                onClick={() => setClaimModalOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="crewcast-modal-body">
              {claimModalError ? (
                <>
                  <div className="crewcast-modal-hint danger">
                    <AlertCircle size={16} />
                    <span>Something went wrong while claiming this reward.</span>
                  </div>
                  <div className="crewcast-modal-error">{claimModalError}</div>
                </>
              ) : (
                <div className="crewcast-modal-hint">
                  <CheckCircle2 size={16} />
                  <span>Your claim has been submitted. You can view it in “My Claimed Rewards”.</span>
                </div>
              )}
            </div>

            <div className="crewcast-modal-actions">
              <button
                type="button"
                className="crewcast-modal-btn secondary"
                onClick={() => setClaimModalOpen(false)}
              >
                Close
              </button>
              {!claimModalError && (
                <button
                  type="button"
                  className="crewcast-modal-btn primary"
                  onClick={() => navigate('/claimed-rewards')}
                >
                  View my claims
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {confirmModalOpen && (
        <div
          className="crewcast-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rewards-toggle-title"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmModalOpen(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirmModalOpen(false); }}
          tabIndex={-1}
        >
          <div className="crewcast-modal-card">
            <div className="crewcast-modal-header">
              <div>
                <h2 id="rewards-toggle-title" className="crewcast-modal-title">
                  {pointsEnabled ? 'Disable Rewards Store?' : 'Enable Rewards Store?'}
                </h2>
                <div className="crewcast-modal-subtitle">
                  {pointsEnabled
                    ? 'Points and rewards UI will be hidden for all employees.'
                    : 'Points and rewards UI will be visible to all employees.'}
                </div>
              </div>
              <button
                type="button"
                className="crewcast-modal-close"
                onClick={() => setConfirmModalOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="crewcast-modal-actions">
              <button
                type="button"
                className="crewcast-modal-btn secondary"
                onClick={() => setConfirmModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crewcast-modal-btn primary"
                onClick={handleToggleRewards}
              >
                {pointsEnabled ? 'Yes, disable' : 'Yes, enable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


